import React from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { UserWithDetails } from '../../types/user';
import { UserAvatar } from './UserAvatar';
import { useUserDetail, UserDetailTab } from './userDetail/useUserDetail';
import { UserDetailsTab } from './userDetail/UserDetailsTab';
import { UserGroupsTab } from './userDetail/UserGroupsTab';
import { UserRolesTab } from './userDetail/UserRolesTab';
import { UserPasswordTab } from './userDetail/UserPasswordTab';

interface UserDetailModalProps {
  user: UserWithDetails | null;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: () => void;
}

/**
 * One account: its identity, its client groups, its roles on this
 * environment, its password. The state and the actions live in
 * `useUserDetail`; each tab is its own component.
 */
export const UserDetailModal: React.FC<UserDetailModalProps> = ({ user, isOpen, onClose, onUserUpdated }) => {
  const { t } = useI18n();
  const state = useUserDetail(user, isOpen, onClose, onUserUpdated);
  const { activeTab, setActiveTab, isLoading, isEditing, error, success, canWriteUsers, showsInternals } = state;

  if (!isOpen || !user) return null;

  const tabs: Array<{ key: UserDetailTab; label: string; testId: string; count?: number; visible: boolean }> = [
    { key: 'details', label: t('tabs.details'), testId: 'tab-user-details', visible: true },
    { key: 'groups', label: t('tabs.groups'), testId: 'tab-user-groups', count: state.groups.length, visible: showsInternals },
    { key: 'roles', label: t('tabs.roles'), testId: 'tab-user-roles', count: state.clientRoles.length, visible: true },
    { key: 'password', label: t('tabs.password'), testId: 'tab-user-password', visible: canWriteUsers },
  ];

  return (
    <div className="modal-overlay" onClick={state.close}>
      <div className="client-detail-modal user-detail-modal" data-testid="modal-user-detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title user-modal-title">
            <UserAvatar username={user.username} firstName={user.firstName} lastName={user.lastName} enabled={user.enabled} size="lg" />
            <div className="user-modal-heading">
              <h2 title={user.fullName}>{user.fullName}</h2>
              <span className="user-modal-sub">
                <span className="mono">{user.username}</span>
                {user.email && <span className="user-modal-email">{user.email}</span>}
              </span>
            </div>
            <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
              {user.enabled ? t('users.enabledBadge') : t('users.disabledBadge')}
            </span>
          </div>
          <button type="button" className="close-button" onClick={state.close} disabled={isLoading} aria-label={t('common.close')}>×</button>
        </div>

        <div className="modal-tabs" role="tablist">
          {tabs.filter((tab) => tab.visible).map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
              data-testid={tab.testId}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}{tab.count !== undefined && <> <span className="ud-tab-count">{tab.count}</span></>}
            </button>
          ))}
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-banner">
              <span>{error}</span>
              <button type="button" onClick={() => state.setError(null)} aria-label={t('common.dismissError')}>x</button>
            </div>
          )}
          {success && (
            <div className="success-banner">
              <span>{success}</span>
              <button type="button" onClick={() => state.setSuccess(null)} aria-label={t('common.close')}>x</button>
            </div>
          )}

          {activeTab === 'details' && <UserDetailsTab state={state} />}
          {activeTab === 'groups' && showsInternals && <UserGroupsTab state={state} />}
          {activeTab === 'roles' && <UserRolesTab state={state} />}
          {activeTab === 'password' && <UserPasswordTab state={state} />}
        </div>

        <div className="modal-actions">
          {activeTab === 'details' && canWriteUsers ? (
            !isEditing ? (
              <>
                <div className="action-group">
                  <button type="button" className="btn btn-outline-danger" data-testid="btn-user-delete" onClick={state.deleteUser} disabled={isLoading}>
                    {t('users.deleteUser')}
                  </button>
                </div>
                <div className="action-group">
                  <button type="button" className="btn btn-primary" data-testid="btn-user-edit" onClick={() => state.setIsEditing(true)} disabled={isLoading}>
                    {t('common.edit')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="action-group">
                  <button type="button" className="btn btn-secondary" data-testid="btn-user-cancel" onClick={state.cancelEdit} disabled={isLoading}>
                    {t('common.cancel')}
                  </button>
                </div>
                <div className="action-group">
                  <button type="button" className="btn btn-primary" data-testid="btn-user-save" onClick={state.saveIdentity} disabled={isLoading}>
                    {isLoading ? t('users.saving') : t('users.saveChanges')}
                  </button>
                </div>
              </>
            )
          ) : (
            <>
              <div className="action-group" />
              <button type="button" className="btn btn-secondary" onClick={state.close} disabled={isLoading}>
                {t('common.close')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
